/* Aria Robotics — Über uns + Service. Ported aus page-company.jsx. */
import { Container, Section, SectionHead, Eyebrow, Btn, Chip, Panel, IconTile, ImageFrame, ImageSlot, go } from '@/ui';
import { AriaData as D } from '@/data';

const MISSION: [string, string, string][] = [
  ['target', 'Vision', 'Ein Zuhause, in dem Technik im Hintergrund hilft und Menschen im Vordergrund leben.'],
  ['compass', 'Haltung', 'Wärme trifft Präzision. Fürsorge mit Substanz. Vertrauen durch Transparenz.'],
];

export function About() {
  return (
    <div>
      <header style={{ background: 'var(--ink)', color: '#fff', backgroundImage: 'radial-gradient(680px 420px at 78% 12%, rgba(74,144,226,0.24), transparent 60%)' }}>
        <Container>
          <div style={{ padding: '84px 0', maxWidth: 720 }}>
            <Chip tone="inverse" dot>Über uns</Chip>
            <h1 style={{ fontSize: 'clamp(36px, 5.2vw, 58px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.03, margin: '20px 0 0' }}>
              Wir bauen Roboter, denen man das Zuhause anvertraut.
            </h1>
            <p style={{ fontSize: 20, fontWeight: 300, color: 'rgba(255,255,255,0.82)', marginTop: 18, lineHeight: 1.55, maxWidth: '52ch' }}>
              Aria Robotics wurde 2019 in Leipzig gegründet — mit der Überzeugung, dass Technik dem Menschen dienen muss, nicht umgekehrt. „Aria" steht für Leichtigkeit, „Robotics" für echte Ingenieurskunst.
            </p>
          </div>
        </Container>
      </header>

      {/* Mission */}
      <Section tone="page">
        <Container>
          <div className="ar-split" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56, alignItems: 'center' }}>
            <ImageFrame id="about-mood" ratio="16/9" src="assets/nestor/emotionen.webp" placeholder="Nestors Ausdruck" style={{ alignSelf: 'center' }} />
            <div>
              <SectionHead eyebrow="Unsere Mission" title="Zeit und Geborgenheit zurückgeben" lead="Hausarbeit hört nie auf. Sie kostet Zeit, die woanders fehlt — bei den Menschen, die uns wichtig sind. Nestor nimmt diese Last ein Stück weit ab, ohne sich in den Vordergrund zu drängen." />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 24 }}>
                {MISSION.map(([ic, h, p]) => (
                  <div key={h} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    <IconTile name={ic} size={40} />
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-100)' }}>{h}</div>
                      <div style={{ fontSize: 14.5, color: 'var(--text-200)', lineHeight: 1.55, marginTop: 2 }}>{p}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Werte */}
      <Section tone="sunken">
        <Container>
          <SectionHead align="center" eyebrow="Unsere Werte" title="Woran wir uns messen lassen" style={{ marginBottom: 44 }} />
          <div className="ar-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
            {D.values.map((v) => (
              <Panel key={v.h} variant="default">
                <IconTile name={v.icon} />
                <h3 style={{ fontSize: 18, color: 'var(--text-100)', margin: '14px 0 8px' }}>{v.h}</h3>
                <p style={{ fontSize: 14.5, color: 'var(--text-200)', lineHeight: 1.6, margin: 0 }}>{v.p}</p>
              </Panel>
            ))}
          </div>
        </Container>
      </Section>

      {/* Timeline */}
      <Section tone="page">
        <Container>
          <SectionHead eyebrow="Unser Weg" title="Sieben Jahre, ein Grundsatz" lead="Erst verlässlich, dann beeindruckend." style={{ marginBottom: 44 }} />
          <div style={{ display: 'grid', gap: 0 }}>
            {D.milestones.map((m) => (
              <div key={m.year} className="ar-row2" style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 28, padding: '24px 0', borderTop: '1px solid var(--border-subtle)' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: 22, fontWeight: 500, color: 'var(--primary-200)' }}>{m.year}</div>
                <div>
                  <h3 style={{ fontSize: 19, color: 'var(--text-100)', margin: '0 0 6px' }}>{m.t}</h3>
                  <p style={{ fontSize: 15, color: 'var(--text-200)', lineHeight: 1.6, margin: 0, maxWidth: '60ch' }}>{m.d}</p>
                </div>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Team */}
      <Section tone="sunken">
        <Container>
          <SectionHead align="center" eyebrow="Menschen hinter Aria" title="Ein kleines Team mit langem Atem" style={{ marginBottom: 44 }} />
          <div className="ar-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
            {D.team.map((t, i) => (
              <Panel key={t.name} variant="default" padding={0} style={{ overflow: 'hidden' }}>
                <div style={{ aspectRatio: '1/1', position: 'relative' }}>
                  <ImageSlot id={`team-${i}`} shape="rect" placeholder={t.name} style={{ width: '100%', height: '100%' }} />
                </div>
                <div style={{ padding: 20 }}>
                  <h3 style={{ fontSize: 17, color: 'var(--text-100)', margin: '0 0 2px' }}>{t.name}</h3>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--primary-200)', marginBottom: 10 }}>{t.role}</div>
                  <p style={{ fontSize: 13.5, color: 'var(--text-200)', lineHeight: 1.55, margin: 0 }}>{t.bio}</p>
                </div>
              </Panel>
            ))}
          </div>
        </Container>
      </Section>

      <Section tone="ink" style={{ padding: '72px 0' }}>
        <Container>
          <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
            <h2 style={{ fontSize: 'clamp(26px, 3.6vw, 38px)', fontWeight: 800, letterSpacing: '-0.02em', color: '#fff', margin: 0 }}>Lernen Sie Nestor kennen.</h2>
            <div style={{ marginTop: 26, display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap' }}>
              <Btn variant="cta" size="lg" onClick={() => go('/nestor-01')}>Nestor 01 ansehen</Btn>
              <Btn variant="ghostInverse" size="lg" onClick={() => go('/kontakt')}>Kontakt aufnehmen</Btn>
            </div>
          </div>
        </Container>
      </Section>
    </div>
  );
}

const FAQ: [string, string][] = [
  ['Wie lange dauert die Einrichtung?', 'Etwa 90 Minuten. Ein Techniker begleitet Sie durch die ersten Aufgaben.'],
  ['Was passiert bei einem Defekt?', 'Ersatzteile kommen meist innerhalb von 48 Stunden aus Leipzig. Drei Jahre Garantie sind inklusive.'],
  ['Kann ich Nestor zurückgeben?', 'Ja. Am Ende der Nutzung nehmen wir Nestor zurück und bereiten Komponenten wieder auf.'],
];

export function Service() {
  const channels: [string, string, string][] = [
    ['phone', D.contact.phone, D.contact.hours],
    ['mail', D.contact.email, 'Antwort meist am selben Werktag'],
    ['message-circle', 'Live-Chat & Video', 'In der Nestor-App integriert'],
  ];
  return (
    <div>
      <header style={{ background: 'var(--ink)', color: '#fff', backgroundImage: 'radial-gradient(680px 420px at 22% 12%, rgba(74,144,226,0.24), transparent 60%)' }}>
        <Container>
          <div style={{ padding: '84px 0', maxWidth: 720 }}>
            <Chip tone="inverse" dot>Service</Chip>
            <h1 style={{ fontSize: 'clamp(36px, 5.2vw, 58px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.03, margin: '20px 0 0' }}>
              Wir bleiben an Ihrer Seite.
            </h1>
            <p style={{ fontSize: 20, fontWeight: 300, color: 'rgba(255,255,255,0.82)', marginTop: 18, lineHeight: 1.55, maxWidth: '50ch' }}>
              Ein Roboter fürs Zuhause ist kein einmaliger Kauf, sondern eine Beziehung. Deshalb kümmern wir uns — vom ersten Tag an und darüber hinaus.
            </p>
          </div>
        </Container>
      </header>

      <Section tone="page">
        <Container>
          <div className="ar-grid-3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20 }}>
            {D.serviceItems.map((s) => (
              <Panel key={s.h} hover>
                <IconTile name={s.icon} />
                <h3 style={{ fontSize: 19, color: 'var(--text-100)', margin: '16px 0 8px' }}>{s.h}</h3>
                <p style={{ fontSize: 14.5, color: 'var(--text-200)', lineHeight: 1.6, margin: 0 }}>{s.p}</p>
              </Panel>
            ))}
          </div>
        </Container>
      </Section>

      {/* Support-Kanäle */}
      <Section tone="sunken">
        <Container>
          <div className="ar-split" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 56, alignItems: 'center' }}>
            <div>
              <SectionHead eyebrow="Persönlicher Support" title="Ein Mensch, kein Menü" lead="Wenn Sie Hilfe brauchen, erreichen Sie eine feste Ansprechperson — ohne Warteschleife und Bandansage." />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 24 }}>
                {channels.map(([ic, a, b]) => (
                  <div key={a} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '14px 16px', background: 'var(--surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                    <IconTile name={ic} size={40} />
                    <div>
                      <div style={{ fontSize: 15.5, fontWeight: 600, color: 'var(--text-100)' }}>{a}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-200)' }}>{b}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <Panel variant="brand" padding={36}>
              <Eyebrow tone="inverse">Häufige Fragen</Eyebrow>
              <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 18 }}>
                {FAQ.map(([q, a]) => (
                  <div key={q}>
                    <div style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>{q}</div>
                    <div style={{ fontSize: 14.5, color: 'rgba(255,255,255,0.8)', lineHeight: 1.55, marginTop: 4 }}>{a}</div>
                  </div>
                ))}
              </div>
            </Panel>
          </div>
        </Container>
      </Section>

      <Section tone="ink" style={{ padding: '72px 0' }}>
        <Container>
          <div style={{ textAlign: 'center', maxWidth: 600, margin: '0 auto' }}>
            <h2 style={{ fontSize: 'clamp(26px, 3.6vw, 38px)', fontWeight: 800, letterSpacing: '-0.02em', color: '#fff', margin: 0 }}>Noch Fragen offen?</h2>
            <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.78)', marginTop: 14 }}>Schreiben Sie uns — wir melden uns persönlich zurück.</p>
            <div style={{ marginTop: 26 }}><Btn variant="cta" size="lg" iconRight="arrow-right" onClick={() => go('/kontakt')}>Zum Kontakt</Btn></div>
          </div>
        </Container>
      </Section>
    </div>
  );
}
